import { Box, Button, IconButton, Stack } from "@mui/material";
import { Lifeline, LifelineType } from "../../domain/model/Lifeline";
import { CustomCardView } from "./CustomCardView";
import { CustomImageView } from "./CustomImageView";
import {
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../data/colors/Colors";
import { CustomPopoverView } from "./CustomPopoverView";
import { useRef, useState } from "react";
import useDoubleClick from "use-double-click";
import { CustomTextView } from "./CustomTextView";
import {
  getSoundCompletePath,
  SoundRepository,
  Sounds,
} from "../../data/repository/sound/SoundRepository";
import { shouldPlaySounds } from "../../core/utils/Utils";

export function LifelinesView({
  lifelines,
  onLifelineClicked,
  selectedColor = COLOR_MAIN,
  defaultColor = COLOR_LIGHT_GRAY,
  disabledColor = "lightGray",
  isMidQuestion,
  size = 60,
}: {
  lifelines: Array<Lifeline>;
  onLifelineClicked?: (id: string) => void;
  selectedColor?: string;
  defaultColor?: string;
  disabledColor?: string;
  isMidQuestion?: boolean;
  size?: number | string;
}) {
  const [selectedLifeline, setSelectedLifeline] = useState<
    Lifeline | undefined
  >();
  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const soundRepo = new SoundRepository();
  return (
    <Box
      maxWidth={"100%"}
      sx={{
        overflow: "auto",
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
        "-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={0.5}>
        {lifelines.map((lifeline) => {
          return (
            <LifelineItemView
              key={lifeline.id}
              size={size}
              lifeline={lifeline}
              onLifelineClicked={
                onLifelineClicked
                  ? (e, lifeline) => {
                      onLifelineClicked(lifeline.id);

                      if (isMidQuestion != undefined && !shouldPlaySounds()) {
                        return;
                      }

                      if (lifeline.selected) {
                        switch (lifeline.type) {
                          case LifelineType.TRAP:
                            soundRepo.play(
                              getSoundCompletePath(
                                Sounds.selectedLifelinePitShovel
                              )
                            );
                            break;

                          case LifelineType.CALL_FRIEND:
                            soundRepo.playWeightedSounds([
                              {
                                soundPath: getSoundCompletePath(
                                  Sounds.selectedLifelineCallAFriendCellPhone
                                ),
                                weight: 25.0,
                              },

                              {
                                soundPath: getSoundCompletePath(
                                  Sounds.selectedLifelineCallAFriendPhoneRing
                                ),
                                weight: 25.0,
                              },
                              {
                                soundPath: getSoundCompletePath(
                                  Sounds.selectedLifelineCallAFriendTelephoneRing1
                                ),
                                weight: 16.6,
                              },

                              {
                                soundPath: getSoundCompletePath(
                                  Sounds.selectedLifelineCallAFriendTelephoneRing2
                                ),
                                weight: 16.7,
                              },

                              {
                                soundPath: getSoundCompletePath(
                                  Sounds.selectedLifelineCallAFriendTelephoneRing3
                                ),
                                weight: 16.7,
                              },
                            ]);
                            break;

                          case LifelineType.DOUBLE_POINT:
                            soundRepo.play(
                              getSoundCompletePath(
                                Sounds.selectedLifelineDoublePointsPowerup
                              )
                            );
                            break;

                          case LifelineType.EXTRA_TIME:
                            soundRepo.play(
                              getSoundCompletePath(
                                Sounds.selectedLifelineAdditionalTimeClockMechanism
                              )
                            );
                            break;

                          case LifelineType.INSURANCE:
                            soundRepo.play(
                              getSoundCompletePath(
                                Sounds.selectedLifelineInsuranceShield
                              )
                            );
                            break;

                          default:
                            soundRepo.play(
                              getSoundCompletePath(Sounds.selectedLifelinePop)
                            );
                            break;
                        }
                      }
                    }
                  : undefined
              }
              onLifelineDoubleClicked={(e, lifeline) => {
                setSelectedLifeline(lifeline);
                setAnchorElPosition({ top: e.clientY, left: e.clientX });
              }}
              selectedColor={selectedColor}
              defaultColor={defaultColor}
              disabledColor={disabledColor}
              isMidQuestion={isMidQuestion}
            />
          );
        })}
      </Stack>

      <CustomPopoverView
        onClose={() => {
          setAnchorElPosition(null);
        }}
        anchorElPosition={anchorElPosition}
        layout={<LifelineInfoView lifeline={selectedLifeline!} />}
      />
    </Box>
  );
}

function LifelineInfoView({ lifeline }: { lifeline: Lifeline }) {
  return (
    <CustomCardView
      padding={2}
      background={COLOR_MAIN}
      borderRadius={6}
      border="solid 1px lightGray"
      children={
        <Stack spacing={1}>
          <CustomTextView
            text={lifeline.name}
            fontWeight="bold"
            textColor={COLOR_WHITE}
            variant="body1"
          />
          <CustomTextView
            text={lifeline.description}
            textColor={COLOR_LIGHT_GRAY}
            variant="caption"
          />
          <CustomTextView
            text={lifeline.shouldChooseBeforeQuestion ? "BEFORE" : "AFTER"}
            fontWeight="bold"
            textColor={COLOR_VERY_LIGHT_GRAY}
            variant="subtitle2"
          />
        </Stack>
      }
    />
  );
}

function LifelineItemView({
  lifeline,
  onLifelineClicked,
  onLifelineDoubleClicked,
  selectedColor,
  defaultColor,
  disabledColor,
  isMidQuestion,
  size,
}: {
  lifeline: Lifeline;
  onLifelineClicked?: (
    e: React.MouseEvent<HTMLButtonElement | Element>,
    lifeline: Lifeline
  ) => void;
  onLifelineDoubleClicked: (
    e: React.MouseEvent<HTMLButtonElement | Element>,
    lifeline: Lifeline
  ) => void;
  selectedColor: string;
  defaultColor: string;
  disabledColor: string;
  isMidQuestion?: boolean;
  size: number | string;
}) {
  const buttonRef = useRef(null);
  useDoubleClick({
    onSingleClick: onLifelineClicked
      ? (e) => {
          if (lifeline.disabled || !allowedToBePicked) return;
          onLifelineClicked(e, lifeline);
        }
      : undefined,
    onDoubleClick: (e) => {
      onLifelineDoubleClicked(e, lifeline);
    },
    ref: buttonRef,
    latency: 250,
  });

  const allowedToBePicked =
    (lifeline.shouldChooseBeforeQuestion && isMidQuestion == false) ||
    (!lifeline.shouldChooseBeforeQuestion && isMidQuestion == true) ||
    isMidQuestion == null;

  const backgroundColor =
    lifeline.disabled || !allowedToBePicked
      ? disabledColor
      : lifeline.selected
      ? selectedColor
      : defaultColor;
  return (
    <IconButton sx={{ p: 0 }} key={lifeline.id} ref={buttonRef}>
      <CustomImageView
        width={size}
        height={size}
        src={lifeline.icon}
        props={{ background: backgroundColor, borderRadius: 40 }}
      />
    </IconButton>
  );
}
